<template>
    <div class="HaulingCalculatorGivePage">
        <SiteHeader/>

        <Container>
            <h1>{{ headingText }}</h1>
            <p class="headingDesc">Create a Quote for a Customer.</p>

            <div class="printPageDiv">
                <Button class="md primary" @click="handlePrintPage">Print PDF</Button>
            </div>

            <form @submit.prevent="handleSubmit">

               <Row>
                    <Column :md="2"><!-- Spacing --></Column>
                    <Column :md="10">
                        <h4 class="sectionTitle">Project Details</h4>
                        <Row>
                            <Column :md="6">
                                <Row>
                                    <Column>
                                        <FormInput
                                            v-model="customerName"
                                            label="Customer Name"
                                        />
                                    </Column>
                                    <Column>
                                        <FormInput
                                            v-model="projectName"
                                            label="Project Name"
                                        />
                                    </Column>
                                    <Column>
                                        <FormInput
                                            v-model="typeOfMaterial"
                                            label="Type of Material"
                                        />
                                    </Column>
                                    <Column>
                                        <FormNumber
                                            v-model="totalAmountOfMaterial"
                                            label="Total Amount of Material"
                                            step="0.01"
                                        />
                                    </Column>
                                </Row>
                            </Column>
                            <Column :md="6">
                                <Row>
                                    <Column>
                                        <FormInput
                                            v-model="loadingLocation"
                                            label="Loading Location"
                                        />
                                    </Column>
                                    <Column>
                                        <FormInput
                                            v-model="dumpingLocation"
                                            label="Dumping Location"
                                        />
                                    </Column>
                                    <Column>
                                        <FormNumber
                                            v-model="fleetSize"
                                            label="Fleet Size"
                                            step="0.01"
                                        />
                                    </Column>

                                    <Column>
                                        <FormSelect
                                            id="unitType"
                                            label="Unit of Measure"
                                            v-model="unitType"
                                        >
                                            <option value="load">Load</option>
                                            <option value="ton">Ton</option>
                                            <option value="cubicyard">Cubic Yard</option>
                                        </FormSelect>
                                    </Column>

                                </Row>
                            </Column>
                        </Row>
                    </Column>
               </Row>

                <Row>
                    <Column :md="2"><!-- Spacing --></Column>
                    <Column :md="10">
                        <h4 class="sectionTitle haulingDetail">Hauling Details</h4>
                        <Row>
                            <Column :md="6">
                                <Row>
                                    <Column>
                                        <FormNumber
                                            v-model="workingDayHours"
                                            label="Total Hours in a Working Day (Start to Finish)"
                                            step="0.01"
                                        />
                                    </Column>
                                    <Column>
                                        <FormNumber
                                            v-model="capacityTons"
                                            label="Truck Capacity (Tons)"
                                            step="0.01"
                                        />
                                    </Column>
                                    <Column>
                                        <FormNumber
                                            v-model="capacityCubicYards"
                                            label="Truck Capacity (Cubic Yards)"
                                            step="0.01"
                                        />
                                    </Column>
                                    <Column>
                                        <FormNumber
                                            v-model="truckBillableRate"
                                            label="Truck Billable Hourly Rate"
                                            step="0.01"
                                        />
                                    </Column>
                                </Row>
                            </Column>
                            <Column :md="6">
                                <Row>
                                    <Column>
                                        <FormNumber
                                            v-model="mobilizationMinutes"
                                            label="Time From Truck Parked to Load Site (Minutes)"
                                            step="0.01"
                                        />
                                    </Column>
                                    <Column>
                                        <FormNumber
                                            v-model="demobilizationMinutes"
                                            label="Time from Last Dump to Park Truck (Minutes)"
                                            step="0.01"
                                        />
                                    </Column>
                                    <Column>
                                        <FormNumber
                                            v-model="loadMinutes"
                                            label="Time at Load Site (Minutes)"
                                            step="0.01"
                                        />
                                    </Column>
                                    <Column>
                                        <FormNumber
                                            v-model="haulMinutes"
                                            label="Time Hauling to Dump Site (Minutes)"
                                            step="0.01"
                                        />
                                    </Column>
                                    <Column>
                                        <FormNumber
                                            v-model="dumpMinutes"
                                            label="Time at Dump Site (Minutes)"
                                            step="0.01"
                                        />
                                    </Column>
                                    <Column>
                                        <FormNumber
                                            v-model="driveMinutes"
                                            label="Time Driving back to Load Site (Minutes)"
                                            step="0.01"
                                        />
                                    </Column>
                                </Row>
                            </Column>
                        </Row>
                    </Column>
                </Row>
                <Row>
                    <Column :md="2">
                         <!-- Spacing -->
                    </Column>
                    <Column :md="10">
                        <h2>
                            <label>Values</label>
                            <Button 
                                type="button" 
                                class="sm primary btnToggleValueResult"
                                @click="handleToggleValueResult"
                            >
                                <Icon :name="showResults ? 'eye' : 'eye-slash'" />
                            </Button>
                        </h2>
                        <div v-if="showResults" class="resultsSection">
                            <Row>
                                <Column :md="6">Mob/Demob Total (Minutes):</Column>
                                <Column :md="6">{{ mobilizationAndDemobilizationTotalMinutes }}</Column>
                            </Row>
                            <Row>
                                <Column :md="6">Cycle Time Total (Minutes):</Column>
                                <Column :md="6">{{ cycleTimeTotal }}</Column>
                            </Row>
                            <Row>
                                <Column :md="6">Cycle Time Total (Hours):</Column>
                                <Column :md="6">{{ cycleTimeTotalHours.toFixed(2) }}</Column>
                            </Row>
                            <Row>
                                <Column :md="6">Loads per Day:</Column>
                                <Column :md="6">{{ loadsPerDay }}</Column>
                            </Row>
                            <Row>
                                <Column :md="6">Total Hauling Hours Worked:</Column>
                                <Column :md="6">{{ totalHaulingHoursWorked.toFixed(2) }}</Column>
                            </Row>
                            <Row>
                                <Column :md="6">Total Hauling Hours Worked (with Mob/Demob):</Column>
                                <Column :md="6">{{ totalAllHoursWorked.toFixed(2) }}</Column>
                            </Row>
                            <Row>
                                <Column :md="6">Total Hours per Load (with Mob/Demob):</Column>
                                <Column :md="6">{{ totalHoursPerLoad.toFixed(2) }}</Column>
                            </Row>
                            <Row>
                                <Column :md="6">Total Hauling Days:</Column>
                                <Column :md="6">{{ totalDays.toFixed(2) }}</Column>
                            </Row>
                        </div>
        
                        <h2>Results</h2>
                        <div class="resultsSection">
                            <Row>
                                <Column :md="6">Price per Load:</Column>
                                <Column :md="6">{{ pricePerLoadResult | formatCurrency }}</Column>
                            </Row>
                            <Row>
                                <Column :md="6">Price per Ton:</Column>
                                <Column :md="6">{{ pricePerTonResult | formatCurrency }}</Column>
                            </Row>
                            <Row>
                                <Column :md="6">Price per Cubic Yard:</Column>
                                <Column :md="6">{{ pricePerCubicYardResult | formatCurrency }}</Column>
                            </Row>
                        </div>
                        
                        <div class="formFooter">
                            <Button type="button" to="/resources/haulingcalc" class="menuBtn primary lg">Menu</Button>
                            <Button type="submit" class="primary lg">Submit</Button>
                        </div>
                    </Column>
                </Row>
            </form>

            <CardPricing
                :title="'Try Dumptek Free'"
                :priceArr="{ currPrice: 20, oldPrice: 30, perDriver: 'Per driver / month' }"
                :features="[
                    { icon: 'infinity', description: 'Unlimited tickets & loads' },
                    { icon: 'check', description: 'Administrator CMS' },
                    { icon: 'check', description: 'Mobile app for drivers' },
                    { icon: 'check', description: 'Generate reports' },
                    { icon: 'check', description: 'Ticket alerts' },
                    { icon: 'check', description: 'Constant updates' }
                ]"
            /> 

        </Container>

        <SiteFooter/>
    </div>
</template>

<script>
import SiteHeader from '../components/SiteHeader';
import SiteFooter from '../components/SiteFooter';
import FormNumber from '@/components/common/FormNumber.vue';
import FormInput from '@/components/common/FormInput.vue';
import Icon from 'vue-awesome/components/Icon';
import CardPricing from '@/components/CardPricing';

export default {
    name: 'HaulingCalculatorGivePage',

    data: () => ({
        headingText: 'Hauling Calculator',
        unitType: null,
        workingDayHours: null,
        mobilizationMinutes: null,
        demobilizationMinutes: null,
        loadMinutes: null,
        haulMinutes: null,
        dumpMinutes: null,
        driveMinutes: null,
        truckBillableRate: null,
        capacityTons: null,
        capacityCubicYards: null,
        pricePerLoadResult: null,
        pricePerTonResult: null,
        pricePerCubicYardResult: null,
        totalAmountOfMaterial: null,
        fleetSize: null,
        showResults: false,
    }),

    components: {
        SiteHeader,
        SiteFooter,
        FormNumber,
        FormInput,
        Icon,
        CardPricing,
    },

    computed: {
        mobilizationAndDemobilizationTotalMinutes() {
            return this.mobilizationMinutes + this.demobilizationMinutes;
        },

        cycleTimeTotal() {
            return this.loadMinutes + this.haulMinutes + this.dumpMinutes + this.driveMinutes;
        },

        cycleTimeTotalHours() {
            return this.cycleTimeTotal / 60;
        },

        loadsPerDay() {
            const workingDayMinutes = this.workingDayHours * 60;
            const availableMinutesOnSite = workingDayMinutes - this.mobilizationAndDemobilizationTotalMinutes;
            return Math.floor(availableMinutesOnSite / this.cycleTimeTotal);
        },

        totalHaulingHoursWorked() {
            return this.cycleTimeTotalHours * this.loadsPerDay;
        },

        totalAllHoursWorked() {
            const mobilizationAndDemobilizationTotalHours = this.mobilizationAndDemobilizationTotalMinutes / 60;
            return mobilizationAndDemobilizationTotalHours + this.totalHaulingHoursWorked;
        },

        totalHoursPerLoad() {
            return this.totalAllHoursWorked / this.loadsPerDay;
        },

        totalDays() {
           
            let totalDay = 0;

            switch (this.unitType) {
                case 'load':
                    totalDay = (this.totalAmountOfMaterial / this.loadsPerDay) / this.fleetSize;
                    break;
                case 'ton':
                    totalDay = ((this.totalAmountOfMaterial / this.capacityTons) / this.loadsPerDay) / this.fleetSize;
                    break;
                case 'cubicyard':
                    totalDay = ((this.totalAmountOfMaterial / this.capacityCubicYards) / this.loadsPerDay) / this.fleetSize;
                    break;
                default:
                    break;
            }

            return totalDay;
        }
    },

    methods: {
        handleSubmit() {
            this.pricePerLoadResult = this.totalHoursPerLoad * this.truckBillableRate;
            this.pricePerTonResult = (this.totalHoursPerLoad * this.truckBillableRate) / this.capacityTons;
            this.pricePerCubicYardResult = (this.totalHoursPerLoad * this.truckBillableRate) / this.capacityCubicYards;

            this.$awn.success('Quote Complete!');
        },
        handlePrintPage() {
            this.headingText = 'Hauling Quote'; 

            this.$nextTick(() => {
                window.print();

                this.headingText = 'Hauling Calculator';
            });
        },
        handleToggleValueResult() {
            this.showResults = !this.showResults;
        }
     
    },
}
</script>

<style lang="scss" scoped>
@import 'globals.scss';

.HaulingCalculatorGivePage {
    display: flex;
    flex-wrap: wrap;
    min-height: 100vh;

    .Container {
        text-align: center;
        margin-bottom: 80px;
    }

    .SiteFooter {
        align-self: flex-end;
    }

    h1 {
        @extend %display-subtitle;
        margin-top: 40px;
        margin-bottom: 15px;
    }

    h2 {
        text-align: left;
        margin-bottom: 1rem;
    }

    p {
        @extend %display-description;
        margin-bottom: 40px;
    }

    .resultsSection .Row {
        & > .Column:first-child {
            text-align: right;
        }

        & > .Column:last-child {
            text-align: left;
        }
    }

   .Button {
        margin-top: 30px;
    } 

    .FormNumber ::v-deep input,
    .FormInput  ::v-deep input {
        @extend %form-control;
        height: 35px;
    }

    .FormInputContainer ::v-deep select {
        height: 35px;
    }

    .btnToggleValueResult {
        margin-left: 10px;
    } 

    .formFooter {
        display: flex;
        justify-content: space-between;
    }

    .printPageDiv {
        display: flex;
        justify-content: end;
        margin-bottom: 15px;
    }
    
    .CardPricing {
        position: fixed;
        top: 20%;
        left: 15px;
    }

    @media screen and (max-width: 1400px) and (min-width: 1000px) {
        .CardPricing ::v-deep .Card {
            padding: 0px !important;

            .cardTitle {
                font-weight: bold;
                font-size: 15px;
                padding-top: 20px;
            }

            h3, strike {
                font-size: 30px;
            }

            h4 {
                font-size: 15px;
            }

            ul {
                padding-left: 30px;
                li {
                    font-size: 13px;
                }
            }

            .trial {
                font-size: 13px;
                padding: 10px;
            }
        }
    }

    @media screen and (max-width: 1000px) {
        .CardPricing {
            position: static;
            top: auto;
            left: auto;
            margin-top:20px;
        }
    }

    /* Project Detail CSS */
    .sectionTitle {
        display: flex;
        justify-content: start;
        margin-bottom: 15px;
    }
    .sectionTitle.haulingDetail {
        margin-top:30px;
    }
    
}

@media print {
  .SiteHeader, .SiteFooter, .CardPricing, .headingDesc {
    display: none;
  }
  .Column.md-10, .Column.md-2 {
    width: 100%;
  }
  .Column.md-5 {
    width: 50%;
  }
}

</style>
